<script>
import { useAlert } from 'dashboard/composables';
import IntegrationsAPI from 'dashboard/api/integrations';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

// Fast poll while waiting for the user to scan / finish connecting
const POLL_INTERVAL_MS = 3000;
// Slow poll while connected, to catch disconnects without hammering UAZAPI
const POLL_INTERVAL_CONNECTED_MS = 20000;

export default {
  components: { NextButton, SpinnerLoader },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      connecting: false,
      healing: false,
      status: 'unknown', // 'connected' | 'connecting' | 'disconnected' | 'unknown'
      qrcode: null,
      profileName: null,
      profilePicUrl: null,
      jid: null,
      lastDisconnectAt: null,
      lastDisconnectReason: null,
      pollTimer: null,
      errorMessage: '',
    };
  },
  computed: {
    statusLabel() {
      if (this.status === 'connected') {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATUS_CONNECTED');
      }
      if (this.status === 'connecting') {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATUS_CONNECTING');
      }
      if (this.status === 'disconnected') {
        return this.$t('INBOX_MGMT.UAZAPI_CONNECTION.STATUS_DISCONNECTED');
      }
      return '—';
    },
    statusColor() {
      return (
        {
          connected: 'text-n-teal-11 bg-n-teal-3',
          connecting: 'text-n-amber-11 bg-n-amber-3',
          disconnected: 'text-n-ruby-11 bg-n-ruby-3',
        }[this.status] || 'text-n-slate-11 bg-n-slate-3'
      );
    },
    statusDotClass() {
      return (
        {
          connected: 'bg-n-teal-9',
          connecting: 'bg-n-amber-9 animate-pulse',
          disconnected: 'bg-n-ruby-9',
        }[this.status] || 'bg-n-slate-9'
      );
    },
  },
  mounted() {
    this.fetchStatus();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    async fetchStatus({ silent = false } = {}) {
      if (!silent) this.loading = true;
      try {
        const { data } = await IntegrationsAPI.fetchUazapiStatus(this.inbox.id);
        const previousStatus = this.status;
        this.status = data.status || 'unknown';
        this.qrcode = data.qrcode || null;
        this.profileName = data.profile_name || null;
        this.profilePicUrl = data.profile_pic_url || null;
        this.jid = data.jid || null;
        this.lastDisconnectAt = data.last_disconnect_at || null;
        this.lastDisconnectReason = data.last_disconnect_reason || null;
        this.errorMessage = '';

        // Toast when transitioning into `connected` while polling
        if (previousStatus === 'connecting' && this.status === 'connected') {
          useAlert(this.$t('INBOX_MGMT.UAZAPI_CONNECTION.CONNECTED_SUCCESS'));
        }

        // Drive polling:
        // - connecting → fast poll (every 3s) until connected
        // - connected → slow poll (every 20s) to catch disconnects
        // - disconnected / unknown → stop polling (user must click Connect)
        if (this.status === 'connecting') {
          this.schedulePoll(POLL_INTERVAL_MS);
        } else if (this.status === 'connected') {
          this.schedulePoll(POLL_INTERVAL_CONNECTED_MS);
        } else {
          this.stopPolling();
        }
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('INBOX_MGMT.UAZAPI_CONNECTION.FETCH_ERROR');
        this.status = 'unknown';
        this.stopPolling();
      } finally {
        this.loading = false;
      }
    },
    async startConnection() {
      this.connecting = true;
      this.errorMessage = '';
      try {
        await IntegrationsAPI.startUazapiConnection(this.inbox.id);
        // Small head-start: fetch immediately, then keep polling.
        await this.fetchStatus({ silent: true });
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('INBOX_MGMT.UAZAPI_CONNECTION.CONNECT_ERROR');
        useAlert(this.errorMessage);
      } finally {
        this.connecting = false;
      }
    },
    async healConfig() {
      this.healing = true;
      this.errorMessage = '';
      try {
        await IntegrationsAPI.healUazapiConfig(this.inbox.id);
        useAlert(this.$t('INBOX_MGMT.UAZAPI_CONNECTION.HEAL_SUCCESS'));
        await this.fetchStatus({ silent: true });
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('INBOX_MGMT.UAZAPI_CONNECTION.HEAL_ERROR');
        useAlert(this.errorMessage);
      } finally {
        this.healing = false;
      }
    },
    schedulePoll(intervalMs = POLL_INTERVAL_MS) {
      this.stopPolling();
      this.pollTimer = setTimeout(() => {
        this.fetchStatus({ silent: true });
      }, intervalMs);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    formatDate(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6 space-y-6">
    <div>
      <h3 class="text-lg font-display font-semibold text-n-slate-12 mb-1">
        {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DESCRIPTION') }}
      </p>
    </div>

    <!-- Loading (initial) -->
    <div v-if="loading" class="flex items-center gap-3">
      <SpinnerLoader />
      <span class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.LOADING') }}
      </span>
    </div>

    <!-- Error -->
    <div
      v-else-if="errorMessage"
      class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-4"
    >
      <p class="text-sm text-n-ruby-11 mb-3">{{ errorMessage }}</p>
      <NextButton
        :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.BTN_RETRY')"
        @click="fetchStatus()"
      />
    </div>

    <!-- Normal render -->
    <template v-else>
      <!-- Status card -->
      <div
        class="rounded-xl border border-n-weak bg-n-solid-2 p-5 flex items-center gap-4"
      >
        <img
          v-if="profilePicUrl"
          :src="profilePicUrl"
          alt=""
          class="w-14 h-14 rounded-full object-cover flex-shrink-0"
        />
        <div
          v-else
          class="w-14 h-14 rounded-full bg-n-slate-3 flex items-center justify-center flex-shrink-0"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            class="w-6 h-6 text-n-slate-11"
            aria-hidden="true"
          >
            <path
              d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
            />
          </svg>
        </div>

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-1">
            <span
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              :class="statusColor"
            >
              <span class="w-1.5 h-1.5 rounded-full" :class="statusDotClass" />
              {{ statusLabel }}
            </span>
          </div>
          <div class="text-sm font-medium text-n-slate-12 truncate">
            {{ profileName || $t('INBOX_MGMT.UAZAPI_CONNECTION.NO_PROFILE') }}
          </div>
          <div v-if="jid" class="text-xs text-n-slate-11 font-mono truncate">
            {{ jid }}
          </div>
        </div>
      </div>

      <!-- Connected: last disconnect info (if any) -->
      <div
        v-if="status === 'connected' && lastDisconnectAt"
        class="text-xs text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.LAST_DISCONNECT') }}:
        {{ formatDate(lastDisconnectAt) }}
        <span v-if="lastDisconnectReason">— {{ lastDisconnectReason }}</span>
      </div>

      <!-- Disconnected: Connect button (loud alert card) -->
      <div
        v-if="status === 'disconnected' || status === 'unknown'"
        class="flex flex-col items-start gap-4 p-5 rounded-xl border-2 border-n-ruby-6 bg-n-ruby-2"
      >
        <div class="flex items-start gap-3">
          <span
            class="w-3 h-3 mt-1.5 rounded-full bg-n-ruby-9 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <h4
              class="text-base font-display font-semibold text-n-ruby-11 mb-1"
            >
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.STATUS_DISCONNECTED') }}
            </h4>
            <p class="text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.DISCONNECTED_HINT') }}
            </p>
            <p v-if="lastDisconnectAt" class="text-xs text-n-slate-11 mt-1">
              {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.LAST_DISCONNECT') }}
              {{ formatDate(lastDisconnectAt) }}
              <span v-if="lastDisconnectReason">
                {{ lastDisconnectReason }}
              </span>
            </p>
          </div>
        </div>
        <NextButton
          solid
          blue
          :is-loading="connecting"
          :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.BTN_CONNECT')"
          icon="i-lucide-qr-code"
          @click="startConnection"
        />
      </div>

      <!-- Connected: heal config + optional reconnect (always available) -->
      <div v-if="status === 'connected'" class="flex flex-col gap-3">
        <div class="flex items-center gap-3 flex-wrap">
          <NextButton
            faded
            blue
            :is-loading="healing"
            :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.BTN_HEAL')"
            icon="i-lucide-wand-2"
            @click="healConfig"
          />
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.HEAL_HINT') }}
          </span>
        </div>
        <div class="flex items-center gap-3 flex-wrap">
          <NextButton
            faded
            slate
            :is-loading="connecting"
            :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.BTN_FORCE_RECONNECT')"
            icon="i-lucide-refresh-cw"
            @click="startConnection"
          />
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.FORCE_RECONNECT_HINT') }}
          </span>
        </div>
      </div>

      <!-- Connecting: QR code + refresh -->
      <div
        v-if="status === 'connecting'"
        class="flex flex-col items-center gap-4 p-6 rounded-xl border border-n-weak bg-n-solid-2"
      >
        <p class="text-sm text-n-slate-12 font-medium text-center">
          {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.QR_HINT') }}
        </p>
        <div v-if="qrcode" class="p-4 bg-white rounded-lg">
          <img :src="qrcode" alt="QR code" class="w-64 h-64 object-contain" />
        </div>
        <div v-else class="flex items-center gap-2">
          <SpinnerLoader />
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.UAZAPI_CONNECTION.GENERATING_QR') }}
          </span>
        </div>
        <NextButton
          faded
          slate
          :is-loading="connecting"
          :label="$t('INBOX_MGMT.UAZAPI_CONNECTION.BTN_REFRESH')"
          icon="i-lucide-refresh-cw"
          @click="startConnection"
        />
      </div>
    </template>
  </div>
</template>
